import { type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { RetryWindow } from './backoff'
import { reconnecting, type StreamOpener, type Waiting } from './reconnecting'

export type { StreamOpener }

export type StreamOptions = {
  queryKey: QueryKey
  open: StreamOpener
  enabled?: boolean
  retry?: RetryWindow
  wait?: Waiting
}

export function useNotificationStream<Frame extends { type: string }>({
  queryKey,
  open,
  enabled = true,
  retry,
  wait,
}: StreamOptions) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return

    const controller = new AbortController()

    void reconnecting<Frame>({
      open,
      signal: controller.signal,
      retry,
      wait,
      onFrame: async (frame) => {
        if (frame.type === 'beat') return

        await queryClient.invalidateQueries({ queryKey })
      },
    })

    return () => controller.abort()
  }, [enabled, open, queryClient, queryKey, retry, wait])
}
